import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
  AbstractComponent,
  BrowserTools,
  NavigationService,
  SolBannerHostComponent,
  SolDebuggerComponent,
  SolDetailsComponent,
  SolErrorPageComponent,
  SolErrorPageType,
  SolLayoutComponent,
  SolLoader,
  SolLoaderComponent,
  SolNavbarComponent,
  SolOverlaysComponent,
  SolPageBannerHostComponent,
  SolPageComponent,
} from '@sol/components';
import { AsmBannerStripHandler } from '@asm/components-platform-shell/handlers/asm-banner-strip.handler';
import { AsmError } from '@asm/components-core/models/asm-error';
import { AsmReadOnlyBannerHandler } from '@asm/components-platform-shell/handlers/asm-read-only-banner.handler';
import { AsmRouteId } from '@asm/components-core/models/asm-route-id';
import { AsmTopbarComponent } from '@asm/components-platform-shell/components/core/asm-topbar/asm-topbar.component';
import { PendoService } from '@asm/components-platform-shell/services/pendo.service';
import { SessionService } from '@asm/components-platform-context/services/session.service';
import { AsmUiStateBadge, AsmUiStateService } from '@asm/components-platform-context/services/asm-ui-state.service';
import { AppState } from './app.state';
import { HostBridgeService } from './host-bridge.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    SolOverlaysComponent,
    SolNavbarComponent,
    SolDetailsComponent,
    SolLoaderComponent,
    SolErrorPageComponent,
    AsmTopbarComponent,
    SolPageComponent,
    SolDebuggerComponent,
    SolLayoutComponent,
    SolBannerHostComponent,
    SolPageBannerHostComponent,
  ],
  templateUrl: './app.component.html',
  host: { '[class]': 'hostClass$()' },
  styleUrl: './app.component.scss',
})
export class AppComponent extends AbstractComponent<AppState> implements OnInit {
  // Dependencies
  private readonly navigationService = inject(NavigationService);
  private readonly sessionService = inject(SessionService);
  private readonly uiStateService = inject(AsmUiStateService);
  private readonly pendoService = inject(PendoService);
  private readonly hostBridgeService = inject(HostBridgeService);

  // Computed
  protected readonly showNavbar$ = this.compute(state => state.showNavbar);
  protected readonly hostClass$ = this.compute(state => state.hostClass);
  protected readonly isEmbed$ = this.compute(state => state.isEmbed);
  protected readonly contentClass$ = this.compute(state => state.contentClass);
  protected readonly activeRouteStatus$ = this.compute(state => state.activeRouteStatus);
  protected readonly SolErrorPageType = SolErrorPageType;
  protected readonly SolLoader = SolLoader;

  // Handlers
  private readonly bannerStrip: AsmBannerStripHandler;
  private readonly readOnlyBanner: AsmReadOnlyBannerHandler;

  constructor() {
    super(AppState.of());

    this.bannerStrip = new AsmBannerStripHandler();
    this.readOnlyBanner = new AsmReadOnlyBannerHandler();

    // Wire NavigationService state into AppState (showNavbar, activeRouteStatus).
    this.navigationService.watch(nss => {
      this.update(state => state.withNavigation(nss.showNavbar, nss.activeRouteStatus));
    });

    this.uiStateService.watchComputed(
      config => config.badges[AsmUiStateBadge.whatsNew] ?? 0,
      count => this.navigationService.setNavbarItemCount(AsmRouteId.whatsNew, count),
    );
  }

  ngOnInit(): void {
    this.sessionService.fetch().subscribe({
      next: () => {
        this.pendoService.bind();

        // In embedded mode: set up all bridge wiring (inbound message listener +
        // outbound NavigationEnd / dialog-state subscriptions), then signal ready.
        // Vue starts the host:init handshake on receipt of child:ready.
        if (BrowserTools.isEmbedded()) {
          this.hostBridgeService.setup();
        }
      },
      error: (error: AsmError) => {
        console.error(error);
      },
    });
  }
}
